import { useCallback, useEffect, useState } from "react";
import { Check, FilePlus2, Loader2, RefreshCw, UsersRound } from "lucide-react";
import { toast } from "sonner";

import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmptyState, PageHeader, StatusBadge, TableSkeleton } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const initialForm = {
  submission_id: "",
  offer_date: "",
  position: "",
  salary: "",
  currency: "USD",
  benefits: "",
  bonus: "",
  allowances: "",
  start_date: "",
  expiry_date: "",
  negotiation_notes: "",
};

export default function Offers() {
  const { can, user } = useAuth();
  const [data, setData] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [revisionOf, setRevisionOf] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [busy, setBusy] = useState(false);
  const canManage = user?.role === "bdm" || can("job:edit");

  const load = useCallback(async () => {
    try {
      const { data: response } = await api.get("/offers", { params: { page_size: 100 } });
      setData(response);
    } catch (error) {
      toast.error(apiErr(error));
      setData({ items: [] });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = async () => {
    try {
      const { data: response } = await api.get("/submissions", { params: { page_size: 100 } });
      setSubmissions(
        (response.items || []).filter((item) => item.internal_review_status === "approved"),
      );
      setRevisionOf(null);
      setForm({ ...initialForm, offer_date: new Date().toISOString().slice(0, 10) });
      setFormOpen(true);
    } catch (error) {
      toast.error(apiErr(error));
    }
  };

  const openRevision = (offer) => {
    setRevisionOf(offer);
    setForm({
      submission_id: offer.submission_id,
      offer_date: new Date().toISOString().slice(0, 10),
      position: offer.position || offer.job_title || "",
      salary: offer.salary || offer.base_comp || "",
      currency: offer.currency || "USD",
      benefits: (offer.benefits || []).join(", "),
      bonus: offer.bonus || "",
      allowances: (offer.allowances || []).join(", "),
      start_date: offer.start_date || "",
      expiry_date: offer.expiry_date || "",
      negotiation_notes: offer.negotiation_notes || "",
    });
    setFormOpen(true);
  };

  const submitOffer = async () => {
    if (!form.submission_id || !form.offer_date) {
      toast.error("Select an approved submission and offer date");
      return;
    }
    setBusy(true);
    const payload = {
      ...form,
      salary: form.salary ? Number(form.salary) : null,
      bonus: form.bonus ? Number(form.bonus) : null,
      benefits: splitList(form.benefits),
      allowances: splitList(form.allowances),
    };
    try {
      if (revisionOf) {
        await api.post(`/offers/${revisionOf.id}/revisions`, payload);
        toast.success("New immutable offer version created");
      } else {
        await api.post("/offers", payload);
        toast.success("Offer draft created");
      }
      setFormOpen(false);
      await load();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Offers & Joining"
        subtitle="Versioned client offers, acceptance evidence, and placement readiness"
        actions={
          canManage ? (
            <Button
              size="sm"
              className="bg-[#255058] text-white hover:bg-[#1c3d43]"
              onClick={openCreate}
              data-testid="offers-create-button"
            >
              <FilePlus2 className="mr-1.5 h-4 w-4" /> New Offer
            </Button>
          ) : null
        }
      />

      <Card className="overflow-hidden">
        {!data ? (
          <div className="p-4">
            <TableSkeleton />
          </div>
        ) : data.items.length === 0 ? (
          <EmptyState icon={FilePlus2} title="No offers yet" />
        ) : (
          <div className="overflow-x-auto scrollbar-thin">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Candidate</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Joining</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((offer) => (
                  <TableRow key={offer.id} data-testid={`offer-row-${offer.id}`}>
                    <TableCell className="font-medium">{offer.candidate_name}</TableCell>
                    <TableCell className="text-sm">{offer.position || offer.job_title}</TableCell>
                    <TableCell>v{offer.version || 1}</TableCell>
                    <TableCell>
                      <StatusBadge value={offer.status} />
                    </TableCell>
                    <TableCell>
                      <StatusBadge value={offer.joining_status || "offer_open"} />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSelected(offer)}
                        data-testid={`offer-manage-${offer.id}`}
                      >
                        Manage
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <OfferForm
        open={formOpen}
        revisionOf={revisionOf}
        form={form}
        setForm={setForm}
        submissions={submissions}
        busy={busy}
        onClose={() => setFormOpen(false)}
        onSubmit={submitOffer}
      />
      <OfferActions
        offer={selected}
        canManage={canManage}
        busy={busy}
        setBusy={setBusy}
        onClose={() => setSelected(null)}
        onChanged={load}
        onRevision={openRevision}
      />
    </div>
  );
}

function OfferForm({ open, revisionOf, form, setForm, submissions, busy, onClose, onSubmit }) {
  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-3xl"
        data-testid="offer-form-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {revisionOf ? `Create Offer v${(revisionOf.version || 1) + 1}` : "Create Offer"}
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="offer-submission">Approved submission</Label>
            <Input
              id="offer-submission"
              value={form.submission_id}
              placeholder="Submission ID"
              disabled={Boolean(revisionOf)}
              onChange={(event) => setForm({ ...form, submission_id: event.target.value })}
              data-testid="offer-submission-input"
            />
            {!revisionOf && submissions.length > 0 && (
              <div className="text-xs text-muted-foreground" data-testid="offer-submission-hint">
                Approved submissions available: {submissions.length}. Paste the selected submission
                ID.
              </div>
            )}
          </div>
          <Field
            label="Offer date"
            type="date"
            value={form.offer_date}
            onChange={(value) => setForm({ ...form, offer_date: value })}
            testId="offer-date-input"
          />
          <Field
            label="Position"
            value={form.position}
            onChange={(value) => setForm({ ...form, position: value })}
            testId="offer-position-input"
          />
          <Field
            label="Salary"
            type="number"
            value={form.salary}
            onChange={(value) => setForm({ ...form, salary: value })}
            testId="offer-salary-input"
          />
          <Field
            label="Currency"
            value={form.currency}
            onChange={(value) => setForm({ ...form, currency: value })}
            testId="offer-currency-input"
          />
          <Field
            label="Bonus"
            type="number"
            value={form.bonus}
            onChange={(value) => setForm({ ...form, bonus: value })}
            testId="offer-bonus-input"
          />
          <Field
            label="Start date"
            type="date"
            value={form.start_date}
            onChange={(value) => setForm({ ...form, start_date: value })}
            testId="offer-start-date-input"
          />
          <Field
            label="Expiry"
            type="date"
            value={form.expiry_date}
            onChange={(value) => setForm({ ...form, expiry_date: value })}
            testId="offer-expiry-input"
          />
          <Field
            label="Benefits (comma separated)"
            value={form.benefits}
            onChange={(value) => setForm({ ...form, benefits: value })}
            testId="offer-benefits-input"
          />
          <Field
            label="Allowances (comma separated)"
            value={form.allowances}
            onChange={(value) => setForm({ ...form, allowances: value })}
            testId="offer-allowances-input"
          />
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="offer-negotiation-notes">Negotiation notes</Label>
            <Textarea
              id="offer-negotiation-notes"
              value={form.negotiation_notes}
              onChange={(event) => setForm({ ...form, negotiation_notes: event.target.value })}
              data-testid="offer-negotiation-notes"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={busy} data-testid="offer-save-button">
            {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Save Version
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OfferActions({ offer, canManage, busy, setBusy, onClose, onChanged, onRevision }) {
  const [acceptance, setAcceptance] = useState({ amount: "", date: "", response: "" });
  const [joining, setJoining] = useState({
    date: "",
    candidate: false,
    client: false,
    candidateEvidence: "",
    clientEvidence: "",
  });
  const call = async (path, payload, success) => {
    setBusy(true);
    try {
      await api.post(`/offers/${offer.id}/${path}`, payload);
      toast.success(success);
      await onChanged();
      onClose();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={Boolean(offer)} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-xl"
        data-testid="offer-actions-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {offer?.candidate_name} · Offer v{offer?.version || 1}
          </DialogTitle>
        </DialogHeader>
        {offer && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Detail label="Status" value={offer.status} />
              <Detail label="Joining" value={offer.joining_status} />
              <Detail label="Position" value={offer.position || offer.job_title} />
              <Detail label="Start" value={offer.start_date} />
            </div>
            {canManage && (
              <Button
                variant="outline"
                onClick={() => {
                  onClose();
                  onRevision(offer);
                }}
                data-testid="offer-revise-button"
              >
                <RefreshCw className="mr-1.5 h-4 w-4" />
                Create New Revision
              </Button>
            )}
            {canManage && offer.status !== "accepted" && (
              <section className="space-y-3 border-t pt-4">
                <h3 className="font-medium">Accept offer</h3>
                <Field
                  label="Final accepted amount"
                  type="number"
                  value={acceptance.amount}
                  onChange={(value) => setAcceptance({ ...acceptance, amount: value })}
                  testId="offer-accept-amount"
                />
                <Field
                  label="Acceptance date"
                  type="date"
                  value={acceptance.date}
                  onChange={(value) => setAcceptance({ ...acceptance, date: value })}
                  testId="offer-accept-date"
                />
                <Button
                  onClick={() =>
                    call(
                      "accept",
                      {
                        final_accepted_amount: Number(acceptance.amount),
                        acceptance_date: acceptance.date,
                        response: acceptance.response,
                      },
                      "Offer accepted; joining task created",
                    )
                  }
                  disabled={busy}
                  data-testid="offer-accept-button"
                >
                  <Check className="mr-1.5 h-4 w-4" />
                  Accept Offer
                </Button>
              </section>
            )}
            {canManage && offer.status === "accepted" && (
              <section className="space-y-3 border-t pt-4">
                <h3 className="font-medium">Confirm joining</h3>
                <Field
                  label="Confirmed joining date"
                  type="date"
                  value={joining.date}
                  onChange={(value) => setJoining({ ...joining, date: value })}
                  testId="offer-joining-date"
                />
                <Field
                  label="Candidate evidence"
                  value={joining.candidateEvidence}
                  onChange={(value) => setJoining({ ...joining, candidateEvidence: value })}
                  testId="offer-candidate-evidence"
                />
                <Field
                  label="Client evidence"
                  value={joining.clientEvidence}
                  onChange={(value) => setJoining({ ...joining, clientEvidence: value })}
                  testId="offer-client-evidence"
                />
                <Button
                  onClick={() =>
                    call(
                      "confirm-joining",
                      {
                        confirmed_date: joining.date,
                        candidate_confirmed: true,
                        client_confirmed: true,
                        candidate_evidence: joining.candidateEvidence,
                        client_evidence: joining.clientEvidence,
                      },
                      "Joining confirmed; placement is now available",
                    )
                  }
                  disabled={busy}
                  data-testid="offer-confirm-joining-button"
                >
                  <UsersRound className="mr-1.5 h-4 w-4" />
                  Confirm Joining
                </Button>
              </section>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, testId, type = "text" }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={testId}>{label}</Label>
      <Input
        id={testId}
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        data-testid={testId}
      />
    </div>
  );
}
function Detail({ label, value }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-medium">{value || "—"}</div>
    </div>
  );
}
function splitList(value) {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}
